import { api } from '@/shared/api'

export type ReferenceKind =
  | 'bundle'
  | 'collection'
  | 'video'
  | 'resource'
  | 'category'
  | 'filterOption'
export type CatalogItemKind = 'collection' | 'video'
export type UserAccessItemKind = 'bundle' | 'collection' | 'video'
export type LinkState = 'all' | 'linked' | 'unlinked'
export type BundleAvailability = 'public' | 'private'
export type VideotecaAccess = 'gated' | 'free_for_all'
export type ManagedMediaKind = 'cover' | 'collectionCover' | 'bundleCover' | 'video' | 'resource'

export type PagedMeta = {
  total: number
  page: number
  limit: number
  hasMore: boolean
}

export type ReferenceRecord = {
  id: string
  kind: ReferenceKind
  title: string
  coverUrl: string | null
  urlSlug: string | null
}

export type ReferencesResponse = {
  data: ReferenceRecord[]
  meta: PagedMeta
}

export type UserAccessListItem = {
  id: string
  email: string | null
  name: string | null
  uscreenUserId: number
  bubbleUserId: string | null
  userId: string | null
  linkedUserEmail: string | null
  createdAt: string
  updatedAt: string
  canDelete: boolean
  accessSummary: {
    bundleCount: number
    collectionCount: number
    videoCount: number
    total: number
  }
}

export type UserAccessListResponse = {
  data: UserAccessListItem[]
  meta: PagedMeta & {
    search: string | null
    linkState: LinkState
  }
}

export type UserAccessDetail = {
  item: {
    id: string
    email: string | null
    name: string | null
    uscreenUserId: number
    bubbleUserId: string | null
    userId: string | null
    linkedUserEmail: string | null
    linkedUserName: string | null
    createdAt: string
    updatedAt: string
    canDelete: boolean
  }
  access: Array<{
    id: string
    itemKind: UserAccessItemKind
    itemId: string | null
    title: string
    coverUrl: string | null
    urlSlug: string | null
    source: string
    createdAt: string
  }>
}

export type BundleListItem = {
  id: string
  title: string
  tariffName: string | null
  mainBundle: boolean
  displayOnCatalog: boolean
  descriptionHtml: string | null
  availability: BundleAvailability
  priceCents: number
  priceCurrency: string
  rentalDuration: string | null
  imageUrl: string | null
  imageKey: string | null
  createdAt: string
  updatedAt: string
  canDelete: boolean
  collectionCount: number
  videoCount: number
}

export type BundleListResponse = {
  data: BundleListItem[]
  meta: PagedMeta & {
    search: string | null
    mainBundle: 'all' | 'true' | 'false'
  }
}

export type BundleDetail = {
  bundle: {
    id: string
    title: string
    tariffName: string | null
    mainBundle: boolean
    displayOnCatalog: boolean
    descriptionHtml: string | null
    priceCents: number
    priceCurrency: string
    rentalDuration: string | null
    availability: BundleAvailability
    imageBucket: string | null
    imageKey: string | null
    imageUrl: string | null
    createdAt: string
    updatedAt: string
    canDelete: boolean
  }
  collections: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    urlSlug: string
    position: number
  }>
  videos: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    urlSlug: string
    position: number
  }>
}

export type CategoryListItem = {
  id: string
  title: string
  urlSlug: string
  color: string | null
  position: number
  createdAt: string
  updatedAt: string
  itemCount: number
}

export type CategoryListResponse = {
  data: CategoryListItem[]
  meta: PagedMeta & {
    search: string | null
  }
}

export type CategoryDetail = {
  category: {
    id: string
    title: string
    urlSlug: string
    color: string | null
    position: number
    createdAt: string
    updatedAt: string
  }
  items: Array<{
    linkId: string
    itemKind: CatalogItemKind
    itemId: string | null
    title: string | null
    coverUrl: string | null
    urlSlug: string | null
    position: number
  }>
}

export type CustomFilterListItem = {
  id: string
  title: string
  position: number
  createdAt: string
  updatedAt: string
  optionCount: number
}

export type CustomFilterListResponse = {
  data: CustomFilterListItem[]
  meta: PagedMeta & {
    search: string | null
  }
}

export type CustomFilterDetail = {
  filter: {
    id: string
    title: string
    position: number
    createdAt: string
    updatedAt: string
  }
  options: Array<{
    id: string
    title: string
    position: number
    createdAt: string
    updatedAt: string
  }>
}

export type ResourceListItem = {
  id: string
  title: string
  fileName: string
  mimeType: string | null
  sizeBytes: number | null
  fileUrl: string | null
  createdAt: string
  updatedAt: string
  collectionCount: number
  videoCount: number
}

export type ResourceListResponse = {
  data: ResourceListItem[]
  meta: PagedMeta & {
    search: string | null
  }
}

export type ResourceDetail = {
  resource: {
    id: string
    title: string
    descriptionHtml: string | null
    fileName: string
    storageBucket: string | null
    storageKey: string | null
    fileUrl: string | null
    mimeType: string | null
    sizeBytes: number | null
    createdAt: string
    updatedAt: string
  }
  collections: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    urlSlug: string
    position: number
  }>
  videos: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    urlSlug: string
    position: number
  }>
}

export type CollectionListItem = {
  id: string
  title: string
  urlSlug: string
  shortDescription: string | null
  coverUrl: string | null
  visibleFromAt: string | null
  access: VideotecaAccess
  totalVideos: number | null
  totalDurationSeconds: number | null
  createdAt: string
  updatedAt: string
  bundleCount: number
  categoryCount: number
  filterOptionCount: number
  resourceCount: number
  videoCount: number
}

export type CollectionListResponse = {
  data: CollectionListItem[]
  meta: PagedMeta & {
    search: string | null
  }
}

export type CollectionDetail = {
  collection: {
    id: string
    title: string
    descriptionHtml: string | null
    shortDescription: string | null
    visibleFromAt: string | null
    access: VideotecaAccess
    coverBucket: string | null
    coverKey: string | null
    coverUrl: string | null
    urlSlug: string
    searchKeywords: string[]
    totalVideos: number | null
    totalDurationSeconds: number | null
    createdAt: string
    updatedAt: string
  }
  bundles: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    position: number
  }>
  categories: Array<{
    linkId: string
    itemId: string
    title: string
    urlSlug: string
    position: number
  }>
  filterOptions: Array<{
    linkId: string
    optionId: string
    optionTitle: string
    optionPosition: number
    filterId: string
    filterTitle: string
  }>
  filterGroups: Array<{
    filterId: string
    filterTitle: string
    filterPosition: number
    selectedLinkId: string | null
    selectedOptionId: string | null
    options: Array<{
      optionId: string
      optionTitle: string
      optionPosition: number
    }>
  }>
  resources: Array<{
    linkId: string
    itemId: string
    title: string
    fileName: string
    mimeType: string | null
    fileUrl: string | null
    position: number
  }>
  videos: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    urlSlug: string
    durationSeconds: number | null
    isFree: boolean
    position: number
  }>
}

export type VideoListItem = {
  id: string
  title: string
  urlSlug: string
  shortDescription: string | null
  coverUrl: string | null
  visibleFromAt: string | null
  access: VideotecaAccess
  durationSeconds: number | null
  fileSizeBytes: number | null
  createdAt: string
  updatedAt: string
  bundleCount: number
  categoryCount: number
  filterOptionCount: number
  resourceCount: number
  collectionCount: number
}

export type VideoListResponse = {
  data: VideoListItem[]
  meta: PagedMeta & {
    search: string | null
  }
}

export type VideoDetail = {
  video: {
    id: string
    title: string
    descriptionHtml: string | null
    shortDescription: string | null
    visibleFromAt: string | null
    access: VideotecaAccess
    coverBucket: string | null
    coverKey: string | null
    coverUrl: string | null
    videoBucket: string | null
    videoKey: string | null
    videoUrl: string | null
    durationSeconds: number | null
    fileSizeBytes: number | null
    urlSlug: string
    searchKeywords: string[]
    createdAt: string
    updatedAt: string
  }
  bundles: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    position: number
  }>
  categories: Array<{
    linkId: string
    itemId: string
    title: string
    urlSlug: string
    position: number
  }>
  filterOptions: Array<{
    linkId: string
    optionId: string
    optionTitle: string
    optionPosition: number
    filterId: string
    filterTitle: string
  }>
  filterGroups: Array<{
    filterId: string
    filterTitle: string
    filterPosition: number
    selectedLinkId: string | null
    selectedOptionId: string | null
    options: Array<{
      optionId: string
      optionTitle: string
      optionPosition: number
    }>
  }>
  resources: Array<{
    linkId: string
    itemId: string
    title: string
    fileName: string
    mimeType: string | null
    fileUrl: string | null
    position: number
  }>
  collections: Array<{
    linkId: string
    itemId: string
    title: string
    coverUrl: string | null
    urlSlug: string
    position: number
    isFree: boolean
  }>
}

export type ManagedUploadUrl = {
  bucket: string
  key: string
  storageUrl: string
  expiresInSeconds: number
  requiredHeaders: Record<string, string>
  uploadMethod: 'PUT'
  uploadUrl: string
}

const withSearch = (search: string) => {
  const normalized = search.trim()
  return normalized.length > 0 ? normalized : undefined
}

export const loadAdminVideoLibraryReferences = async (
  kind: ReferenceKind,
  page: number,
  limit: number,
  search: string
) =>
  api.get<ReferencesResponse>('/api/v1/admin/video-library/references', {
    params: {
      kind,
      page: String(page),
      limit: String(limit),
      ...(withSearch(search) ? { search: withSearch(search) as string } : {}),
    },
  })

export const loadAdminUserAccessList = async (filters: {
  search: string
  linkState: LinkState
  page: number
  limit: number
}) =>
  api.get<UserAccessListResponse>('/api/v1/admin/video-library/user-access', {
    params: {
      page: String(filters.page),
      limit: String(filters.limit),
      linkState: filters.linkState,
      ...(withSearch(filters.search) ? { search: withSearch(filters.search) as string } : {}),
    },
  })

export const loadAdminUserAccessDetail = async (id: string) =>
  api.get<UserAccessDetail>(`/api/v1/admin/video-library/user-access/${id}`)

export const createAdminUserAccessGrant = async (input: {
  id: string
  itemKind: UserAccessItemKind
  itemId: string
}) =>
  api.post<{ success: true; detail: UserAccessDetail }>(
    `/api/v1/admin/video-library/user-access/${input.id}/access`,
    {
      itemKind: input.itemKind,
      itemId: input.itemId,
    }
  )

export const deleteAdminUserAccessGrant = async (input: { id: string; accessId: string }) =>
  api.del<{ success: true; detail: UserAccessDetail }>(
    `/api/v1/admin/video-library/user-access/${input.id}/access/${input.accessId}`
  )

export const deleteAdminUserAccessEntry = async (id: string) =>
  api.del<{ success: true; deletedId: string }>(`/api/v1/admin/video-library/user-access/${id}`)

export const loadAdminBundlesList = async (filters: {
  search: string
  mainBundle: 'all' | 'true' | 'false'
  page: number
  limit: number
}) =>
  api.get<BundleListResponse>('/api/v1/admin/video-library/bundles', {
    params: {
      page: String(filters.page),
      limit: String(filters.limit),
      mainBundle: filters.mainBundle,
      ...(withSearch(filters.search) ? { search: withSearch(filters.search) as string } : {}),
    },
  })

export const loadAdminBundleDetail = async (id: string) =>
  api.get<BundleDetail>(`/api/v1/admin/video-library/bundles/${id}`)

export const createAdminBundle = async (input: {
  title: string
  tariffName?: string | null
  mainBundle?: boolean
  displayOnCatalog?: boolean
  descriptionHtml?: string | null
  priceCents?: number
  priceCurrency?: string
  rentalDuration?: string | null
  availability?: BundleAvailability
  imageBucket?: string | null
  imageKey?: string | null
  imageUrl?: string | null
}) =>
  api.post<{ success: true; detail: BundleDetail }>('/api/v1/admin/video-library/bundles', input)

export const updateAdminBundle = async (
  id: string,
  input: {
    title?: string
    tariffName?: string | null
    mainBundle?: boolean
    displayOnCatalog?: boolean
    descriptionHtml?: string | null
    priceCents?: number
    priceCurrency?: string
    rentalDuration?: string | null
    availability?: BundleAvailability
    imageBucket?: string | null
    imageKey?: string | null
    imageUrl?: string | null
  }
) =>
  api.patch<{ success: true; detail: BundleDetail }>(
    `/api/v1/admin/video-library/bundles/${id}`,
    input
  )

export const addAdminBundleItem = async (input: {
  id: string
  itemKind: CatalogItemKind
  itemId: string
}) =>
  api.post<{ success: true; detail: BundleDetail }>(
    `/api/v1/admin/video-library/bundles/${input.id}/items`,
    {
      itemKind: input.itemKind,
      itemId: input.itemId,
    }
  )

export const deleteAdminBundleItem = async (input: {
  id: string
  itemKind: CatalogItemKind
  linkId: string
}) =>
  api.del<{ success: true; detail: BundleDetail }>(
    `/api/v1/admin/video-library/bundles/${input.id}/items/${input.itemKind}/${input.linkId}`
  )

export const deleteAdminBundle = async (id: string) =>
  api.del<{ success: true; deletedId: string }>(`/api/v1/admin/video-library/bundles/${id}`)

export const loadAdminCategoriesList = async (filters: {
  search: string
  page: number
  limit: number
}) =>
  api.get<CategoryListResponse>('/api/v1/admin/video-library/categories', {
    params: {
      page: String(filters.page),
      limit: String(filters.limit),
      ...(withSearch(filters.search) ? { search: withSearch(filters.search) as string } : {}),
    },
  })

export const loadAdminCategoryDetail = async (id: string) =>
  api.get<CategoryDetail>(`/api/v1/admin/video-library/categories/${id}`)

export const createAdminCategory = async (input: {
  title: string
  urlSlug: string
  color?: string | null
  position?: number
}) =>
  api.post<{ success: true; detail: CategoryDetail }>(
    '/api/v1/admin/video-library/categories',
    input
  )

export const updateAdminCategory = async (
  id: string,
  input: {
    title?: string
    urlSlug?: string
    color?: string | null
    position?: number
  }
) =>
  api.patch<{ success: true; detail: CategoryDetail }>(
    `/api/v1/admin/video-library/categories/${id}`,
    input
  )

export const addAdminCategoryItem = async (input: {
  id: string
  itemKind: CatalogItemKind
  itemId: string
}) =>
  api.post<{ success: true; detail: CategoryDetail }>(
    `/api/v1/admin/video-library/categories/${input.id}/items`,
    {
      itemKind: input.itemKind,
      itemId: input.itemId,
    }
  )

export const updateAdminCategoryItemPosition = async (input: {
  id: string
  linkId: string
  position: number
}) =>
  api.patch<{ success: true; detail: CategoryDetail }>(
    `/api/v1/admin/video-library/categories/${input.id}/items/${input.linkId}`,
    {
      position: input.position,
    }
  )

export const deleteAdminCategoryItem = async (input: {
  id: string
  itemKind: CatalogItemKind
  linkId: string
}) =>
  api.del<{ success: true; detail: CategoryDetail }>(
    `/api/v1/admin/video-library/categories/${input.id}/items/${input.itemKind}/${input.linkId}`
  )

export const deleteAdminCategory = async (id: string) =>
  api.del<{ success: true; deletedId: string }>(`/api/v1/admin/video-library/categories/${id}`)

export const loadAdminCustomFiltersList = async (filters: {
  search: string
  page: number
  limit: number
}) =>
  api.get<CustomFilterListResponse>('/api/v1/admin/video-library/custom-filters', {
    params: {
      page: String(filters.page),
      limit: String(filters.limit),
      ...(withSearch(filters.search) ? { search: withSearch(filters.search) as string } : {}),
    },
  })

export const loadAdminCustomFilterDetail = async (id: string) =>
  api.get<CustomFilterDetail>(`/api/v1/admin/video-library/custom-filters/${id}`)

export const createAdminCustomFilter = async (input: { title: string; position?: number }) =>
  api.post<{ success: true; detail: CustomFilterDetail }>(
    '/api/v1/admin/video-library/custom-filters',
    input
  )

export const updateAdminCustomFilter = async (
  id: string,
  input: { title?: string; position?: number }
) =>
  api.patch<{ success: true; detail: CustomFilterDetail }>(
    `/api/v1/admin/video-library/custom-filters/${id}`,
    input
  )

export const createAdminCustomFilterOption = async (input: {
  id: string
  title: string
  position?: number
}) =>
  api.post<{ success: true; detail: CustomFilterDetail }>(
    `/api/v1/admin/video-library/custom-filters/${input.id}/options`,
    {
      title: input.title,
      position: input.position,
    }
  )

export const updateAdminCustomFilterOption = async (input: {
  id: string
  optionId: string
  title?: string
  position?: number
}) =>
  api.patch<{ success: true; detail: CustomFilterDetail }>(
    `/api/v1/admin/video-library/custom-filters/${input.id}/options/${input.optionId}`,
    {
      title: input.title,
      position: input.position,
    }
  )

export const deleteAdminCustomFilterOption = async (input: { id: string; optionId: string }) =>
  api.del<{ success: true; detail: CustomFilterDetail }>(
    `/api/v1/admin/video-library/custom-filters/${input.id}/options/${input.optionId}`
  )

export const deleteAdminCustomFilter = async (id: string) =>
  api.del<{ success: true; deletedId: string }>(`/api/v1/admin/video-library/custom-filters/${id}`)

export const loadAdminResourcesList = async (filters: {
  search: string
  page: number
  limit: number
}) =>
  api.get<ResourceListResponse>('/api/v1/admin/video-library/resources', {
    params: {
      page: String(filters.page),
      limit: String(filters.limit),
      ...(withSearch(filters.search) ? { search: withSearch(filters.search) as string } : {}),
    },
  })

export const loadAdminResourceDetail = async (id: string) =>
  api.get<ResourceDetail>(`/api/v1/admin/video-library/resources/${id}`)

export const createAdminResource = async (input: {
  title: string
  descriptionHtml?: string | null
  fileName: string
  storageBucket?: string | null
  storageKey?: string | null
  fileUrl?: string | null
  mimeType?: string | null
  sizeBytes?: number | null
}) =>
  api.post<{ success: true; detail: ResourceDetail }>(
    '/api/v1/admin/video-library/resources',
    input
  )

export const updateAdminResource = async (
  id: string,
  input: {
    title?: string
    descriptionHtml?: string | null
    fileName?: string
    storageBucket?: string | null
    storageKey?: string | null
    fileUrl?: string | null
    mimeType?: string | null
    sizeBytes?: number | null
  }
) =>
  api.patch<{ success: true; detail: ResourceDetail }>(
    `/api/v1/admin/video-library/resources/${id}`,
    input
  )

export const deleteAdminResource = async (id: string) =>
  api.del<{ success: true; deletedId: string }>(`/api/v1/admin/video-library/resources/${id}`)

export const loadAdminCollectionsList = async (filters: {
  search: string
  page: number
  limit: number
}) =>
  api.get<CollectionListResponse>('/api/v1/admin/video-library/collections', {
    params: {
      page: String(filters.page),
      limit: String(filters.limit),
      ...(withSearch(filters.search) ? { search: withSearch(filters.search) as string } : {}),
    },
  })

export const loadAdminCollectionDetail = async (id: string) =>
  api.get<CollectionDetail>(`/api/v1/admin/video-library/collections/${id}`)

export const createAdminCollection = async (input: {
  title: string
  descriptionHtml?: string | null
  shortDescription?: string | null
  visibleFromAt?: string | null
  access?: VideotecaAccess
  coverBucket?: string | null
  coverKey?: string | null
  coverUrl?: string | null
  urlSlug: string
  searchKeywords?: string[]
}) =>
  api.post<{ success: true; detail: CollectionDetail }>(
    '/api/v1/admin/video-library/collections',
    input
  )

export const updateAdminCollection = async (
  id: string,
  input: {
    title?: string
    descriptionHtml?: string | null
    shortDescription?: string | null
    visibleFromAt?: string | null
    access?: VideotecaAccess
    coverBucket?: string | null
    coverKey?: string | null
    coverUrl?: string | null
    urlSlug?: string
    searchKeywords?: string[]
  }
) =>
  api.patch<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${id}`,
    input
  )

export const deleteAdminCollection = async (id: string) =>
  api.del<{ success: true; deletedId: string }>(`/api/v1/admin/video-library/collections/${id}`)

export const addAdminCollectionBundle = async (input: { id: string; bundleId: string }) =>
  api.post<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/bundles`,
    { bundleId: input.bundleId }
  )

export const deleteAdminCollectionBundle = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/bundles/${input.linkId}`
  )

export const addAdminCollectionCategory = async (input: { id: string; categoryId: string }) =>
  api.post<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/categories`,
    { categoryId: input.categoryId }
  )

export const deleteAdminCollectionCategory = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/categories/${input.linkId}`
  )

export const addAdminCollectionFilterOption = async (input: { id: string; optionId: string }) =>
  api.post<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/filter-options`,
    { optionId: input.optionId }
  )

export const deleteAdminCollectionFilterOption = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/filter-options/${input.linkId}`
  )

export const addAdminCollectionResource = async (input: { id: string; resourceId: string }) =>
  api.post<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/resources`,
    { resourceId: input.resourceId }
  )

export const updateAdminCollectionResourcePosition = async (input: {
  id: string
  linkId: string
  position: number
}) =>
  api.patch<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/resources/${input.linkId}`,
    { position: input.position }
  )

export const deleteAdminCollectionResource = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/resources/${input.linkId}`
  )

export const addAdminCollectionVideo = async (input: {
  id: string
  videoId: string
  isFree?: boolean
}) =>
  api.post<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/videos`,
    {
      videoId: input.videoId,
      isFree: input.isFree,
    }
  )

export const updateAdminCollectionVideoLink = async (input: {
  id: string
  linkId: string
  position?: number
  isFree?: boolean
}) =>
  api.patch<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/videos/${input.linkId}`,
    {
      position: input.position,
      isFree: input.isFree,
    }
  )

export const deleteAdminCollectionVideo = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: CollectionDetail }>(
    `/api/v1/admin/video-library/collections/${input.id}/videos/${input.linkId}`
  )

export const loadAdminVideosList = async (filters: {
  search: string
  page: number
  limit: number
}) =>
  api.get<VideoListResponse>('/api/v1/admin/video-library/videos', {
    params: {
      page: String(filters.page),
      limit: String(filters.limit),
      ...(withSearch(filters.search) ? { search: withSearch(filters.search) as string } : {}),
    },
  })

export const loadAdminVideoDetail = async (id: string) =>
  api.get<VideoDetail>(`/api/v1/admin/video-library/videos/${id}`)

export const createAdminVideo = async (input: {
  title: string
  descriptionHtml?: string | null
  shortDescription?: string | null
  visibleFromAt?: string | null
  access?: VideotecaAccess
  coverBucket?: string | null
  coverKey?: string | null
  coverUrl?: string | null
  videoBucket?: string | null
  videoKey?: string | null
  videoUrl?: string | null
  durationSeconds?: number | null
  fileSizeBytes?: number | null
  urlSlug: string
  searchKeywords?: string[]
}) => api.post<{ success: true; detail: VideoDetail }>('/api/v1/admin/video-library/videos', input)

export const updateAdminVideo = async (
  id: string,
  input: {
    title?: string
    descriptionHtml?: string | null
    shortDescription?: string | null
    visibleFromAt?: string | null
    access?: VideotecaAccess
    coverBucket?: string | null
    coverKey?: string | null
    coverUrl?: string | null
    videoBucket?: string | null
    videoKey?: string | null
    videoUrl?: string | null
    durationSeconds?: number | null
    fileSizeBytes?: number | null
    urlSlug?: string
    searchKeywords?: string[]
  }
) =>
  api.patch<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${id}`,
    input
  )

export const deleteAdminVideo = async (id: string) =>
  api.del<{ success: true; deletedId: string }>(`/api/v1/admin/video-library/videos/${id}`)

export const addAdminVideoBundle = async (input: { id: string; bundleId: string }) =>
  api.post<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/bundles`,
    { bundleId: input.bundleId }
  )

export const deleteAdminVideoBundle = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/bundles/${input.linkId}`
  )

export const addAdminVideoCategory = async (input: { id: string; categoryId: string }) =>
  api.post<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/categories`,
    { categoryId: input.categoryId }
  )

export const deleteAdminVideoCategory = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/categories/${input.linkId}`
  )

export const addAdminVideoFilterOption = async (input: { id: string; optionId: string }) =>
  api.post<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/filter-options`,
    { optionId: input.optionId }
  )

export const deleteAdminVideoFilterOption = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/filter-options/${input.linkId}`
  )

export const addAdminVideoResource = async (input: { id: string; resourceId: string }) =>
  api.post<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/resources`,
    { resourceId: input.resourceId }
  )

export const updateAdminVideoResourcePosition = async (input: {
  id: string
  linkId: string
  position: number
}) =>
  api.patch<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/resources/${input.linkId}`,
    { position: input.position }
  )

export const deleteAdminVideoResource = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/resources/${input.linkId}`
  )

export const addAdminVideoCollection = async (input: {
  id: string
  collectionId: string
  isFree?: boolean
}) =>
  api.post<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/collections`,
    {
      collectionId: input.collectionId,
      isFree: input.isFree,
    }
  )

export const updateAdminVideoCollectionLink = async (input: {
  id: string
  linkId: string
  position?: number
  isFree?: boolean
}) =>
  api.patch<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/collections/${input.linkId}`,
    {
      position: input.position,
      isFree: input.isFree,
    }
  )

export const deleteAdminVideoCollection = async (input: { id: string; linkId: string }) =>
  api.del<{ success: true; detail: VideoDetail }>(
    `/api/v1/admin/video-library/videos/${input.id}/collections/${input.linkId}`
  )

export const createAdminManagedUploadUrl = async (input: {
  kind: ManagedMediaKind
  fileName?: string
  contentType?: string
  entityId?: string
}) => api.post<{ success: true; upload: ManagedUploadUrl }>('/api/v1/admin/media/upload-url', input)

export const deleteAdminManagedObject = async (input: {
  key: string
  kind?: ManagedMediaKind
  bucket?: string
}) =>
  api.del<{
    success: true
    deleted: { bucket: string; key: string; kind: ManagedMediaKind | null }
  }>('/api/v1/admin/media/object', {
    body: JSON.stringify(input),
    headers: {
      'Content-Type': 'application/json',
    },
  })
